import BugReportIcon from "@mui/icons-material/BugReport";
import CodeIcon from "@mui/icons-material/Code";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DesignServicesIcon from "@mui/icons-material/DesignServices";
import DifferenceIcon from "@mui/icons-material/Difference";
import DnsIcon from "@mui/icons-material/Dns";
import EmailIcon from "@mui/icons-material/Email";
import FactCheckIcon from "@mui/icons-material/FactCheck";
import HubIcon from "@mui/icons-material/Hub";
import Inventory2Icon from "@mui/icons-material/Inventory2";
import LayersIcon from "@mui/icons-material/Layers";
import LibraryAddCheckIcon from "@mui/icons-material/LibraryAddCheck";
import CampaignIcon from "@mui/icons-material/Campaign";
import CableIcon from "@mui/icons-material/Cable";
import LibraryBooksIcon from "@mui/icons-material/LibraryBooks";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import LogoutIcon from "@mui/icons-material/Logout";
import ManageSearchIcon from "@mui/icons-material/ManageSearch";
import MarkEmailReadIcon from "@mui/icons-material/MarkEmailRead";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import PreviewIcon from "@mui/icons-material/Preview";
import QrCode2Icon from "@mui/icons-material/QrCode2";
import QueryStatsIcon from "@mui/icons-material/QueryStats";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import ScienceIcon from "@mui/icons-material/Science";
import SendIcon from "@mui/icons-material/Send";
import SettingsIcon from "@mui/icons-material/Settings";
import StorageIcon from "@mui/icons-material/Storage";
import SyncAltIcon from "@mui/icons-material/SyncAlt";
import TableChartIcon from "@mui/icons-material/TableChart";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import VpnKeyIcon from "@mui/icons-material/VpnKey";
import WidgetsIcon from "@mui/icons-material/Widgets";
import { type SvgIconComponent } from "@mui/icons-material";
import { ROUTES } from "../../route-paths";

/** A single internal tool surfaced on the home launcher. */
export type InternalTool = {
  /** Display name. */
  name: string;
  /** One-line description of what the tool does, for the card. */
  description: string;
  /** Router path the card navigates to. */
  path: string;
  /** Material icon shown in the card. */
  icon: SvgIconComponent;
};

/** A named group of internal tools. */
export type ToolCategory = {
  /** Category heading and sidebar label. */
  category: string;
  /** Tools in this category. */
  tools: InternalTool[];
};

/**
 * Single source of truth for the internal tools home launcher.
 *
 * Categories are derived from what each tool actually does (verified against
 * each page's implementation), not from its name. Adding a tool is a one-line
 * entry here; the home page and its sidebar render entirely from this list.
 */
export const TOOL_CATEGORIES: ToolCategory[] = [
  {
    category: "Document ingestion",
    tools: [
      {
        name: "Scan logs",
        description:
          "Per-document ingestion results, newest first — both pipelines.",
        path: ROUTES.DOC_INGESTION_LOGS,
        icon: ReceiptLongIcon,
      },
      {
        name: "Scan statistics",
        description:
          "Ingestion volume by company, contact, user, and schema — both pipelines.",
        path: ROUTES.DOC_INGESTION_STATISTICS,
        icon: QueryStatsIcon,
      },
      {
        name: "Schema editor",
        description:
          "Draw fingerprint anchors and field extractors on a document. Legacy schema-matching pipeline.",
        path: ROUTES.DOC_INGESTION_SCHEMA_EDITOR,
        icon: DesignServicesIcon,
      },
      {
        name: "Schema list",
        description:
          "Search schemas, map them to companies, and bulk match. Legacy schema-matching pipeline.",
        path: ROUTES.DOC_INGESTION_SCHEMAS,
        icon: TableChartIcon,
      },
      {
        name: "Schema test lists",
        description:
          "Pass/fail results for a schema's saved test snapshots. Legacy schema-matching pipeline.",
        path: ROUTES.DOC_INGESTION_SCHEMA_TEST_LISTS,
        icon: FactCheckIcon,
      },
      {
        name: "Outlook integrations",
        description:
          "Mailboxes documents arrive in, and the domains allowed to reply from them.",
        path: ROUTES.DOC_INGESTION_OUTLOOK_INTEGRATIONS,
        icon: MarkEmailReadIcon,
      },
      {
        name: "Ad-hoc ingestion preview",
        description:
          "Upload a document and preview extraction, apply plan, and exemplars without touching real intake. New document-extraction pipeline.",
        path: ROUTES.ADHOC_INGESTION_PREVIEW,
        icon: PreviewIcon,
      },
      {
        name: "Batch ingestion preview",
        description:
          "Upload a set of documents for one company and audit the per-document decision grid in one run — an onboarding dry-run. New document-extraction pipeline.",
        path: ROUTES.ADHOC_INGESTION_PREVIEW_BATCH,
        icon: LibraryAddCheckIcon,
      },
      {
        name: "Ingestion test runs",
        description:
          "Inspect an end-to-end test run by id: lifecycle, dispatch context, decision plan, and cost. New document-extraction pipeline.",
        path: ROUTES.INGESTION_TEST_RUNS,
        icon: BugReportIcon,
      },
      {
        name: "Ingestion prefill inspector",
        description:
          "Inspect the zero-LLM billing-party and service prefill verdicts for a company. New document-extraction pipeline.",
        path: ROUTES.INGESTION_INSPECTOR,
        icon: ManageSearchIcon,
      },
      {
        name: "Doc extraction evals",
        description:
          "Build eval sets and run extraction models against them. New document-extraction pipeline.",
        path: ROUTES.DOC_EXTRACTION_EVALS,
        icon: ScienceIcon,
      },
    ],
  },
  {
    category: "Integrations & EDI",
    tools: [
      {
        name: "EDI",
        description: "View and send inbound and outbound EDI transactions.",
        path: ROUTES.EDI,
        icon: HubIcon,
      },
      {
        name: "Invoice emails",
        description: "Invoice email transmission logs and delivery status.",
        path: ROUTES.INVOICE_TRANSMISSIONS,
        icon: EmailIcon,
      },
      {
        name: "Tenedi connections",
        description:
          "Set up a carrier and customer for EDI through Tenedi: both sides' EDI IDs, the partnership, and a readiness checklist. Opens the outbound test bench when it is ready.",
        path: ROUTES.TENEDI_CONNECTIONS,
        icon: CableIcon,
      },
    ],
  },
  {
    category: "Configuration & data",
    tools: [
      {
        name: "Config migration",
        description: "Copy a company's configuration between environments.",
        path: ROUTES.CONFIG_MIGRATION,
        icon: SyncAltIcon,
      },
      {
        name: "Company configurations",
        description: "Per-company operational settings.",
        path: ROUTES.COMPANY_CONFIGURATIONS,
        icon: SettingsIcon,
      },
      {
        name: "Notification cutover",
        description:
          "Flip notification events between the legacy path and the new engine (off / shadow / full) for this environment.",
        path: ROUTES.NOTIFICATION_CUTOVER,
        icon: NotificationsActiveIcon,
      },
      {
        name: "Notification shadow",
        description:
          "Visualize shadow-mode comparisons between the legacy notification path and the engine (match/mismatch/orphan).",
        path: ROUTES.NOTIFICATION_SHADOW,
        icon: DifferenceIcon,
      },
      {
        name: "Crown data import",
        description: "Import Crown tariffs, orders, users, and more.",
        path: ROUTES.CROWN_DATA_IMPORT,
        icon: StorageIcon,
      },
      {
        name: "Piece backfill",
        description:
          "Create the missing pieces for a company's existing packages, so piece tracking works on older orders. Survey and dry run before executing.",
        path: ROUTES.PIECE_BACKFILL,
        icon: WidgetsIcon,
      },
    ],
  },
  {
    category: "Testing & tools",
    tools: [
      {
        name: "Test integration orders",
        description:
          "Create synthetic EDI or API orders in a test environment through the real integration create paths, so scenarios keyed off order source (duplicate-order merging) can be reproduced.",
        path: ROUTES.TEST_INTEGRATION_ORDERS,
        icon: SendIcon,
      },
      {
        name: "Outbound test bench",
        description:
          "Walk TENEDI orders through the real lifecycle services, or fire one 990, 214 or 210, and see each send land in Tenedi.",
        path: ROUTES.OUTBOUND_TEST_BENCH,
        icon: LocalShippingIcon,
      },
      {
        name: "Test orders",
        description: "Create test orders with expected charges.",
        path: ROUTES.TEST_ORDERS,
        icon: Inventory2Icon,
      },
      {
        name: "Code gen",
        description: "Generate backend boilerplate from a Prisma model.",
        path: ROUTES.CODE_GEN,
        icon: CodeIcon,
      },
      {
        name: "Barcode generator",
        description: "Bulk-generate scannable barcodes; print or share a link.",
        path: ROUTES.BARCODE_GENERATOR,
        icon: QrCode2Icon,
      },
      {
        name: "Storybook",
        description: "Component library (opens externally).",
        path: ROUTES.STORYBOOK,
        icon: MenuBookIcon,
      },
    ],
  },
  {
    category: "Infrastructure & ops",
    tools: [
      {
        name: "Render",
        description: "Render deploys grouped by environment.",
        path: ROUTES.RENDER,
        icon: DnsIcon,
      },
      {
        name: "Queues",
        description: "Background job queue dashboard.",
        path: ROUTES.QUEUES,
        icon: LayersIcon,
      },
      {
        name: "SFTP uploads",
        description: "Outbound SFTP uploads; retry failures.",
        path: ROUTES.SFTP_UPLOADS,
        icon: UploadFileIcon,
      },
    ],
  },
  {
    category: "Admin",
    tools: [
      {
        name: "API keys",
        description: "Manage external API keys and webhooks.",
        path: ROUTES.API_KEYS_ADMIN,
        icon: VpnKeyIcon,
      },
      {
        name: "Documentation",
        description:
          "Topics and HTML help documents shown to every customer on Settings > Documentation.",
        path: ROUTES.DOCUMENTATION_ADMIN,
        icon: LibraryBooksIcon,
      },
      {
        name: "Organization messages",
        description:
          "Send a message to every user of one company or of every company: feature announcements, outages and follow-ups.",
        path: ROUTES.ORGANIZATION_MESSAGES,
        icon: CampaignIcon,
      },
      {
        name: "Order mirror",
        description:
          "Configure the environments production orders are copied to, choose which companies are captured, and run on-demand back-syncs.",
        path: ROUTES.ORDER_MIRROR,
        icon: ContentCopyIcon,
      },
      {
        name: "User sessions",
        description:
          "End a customer's live sessions and archive their users when an account is shut off.",
        path: ROUTES.USER_SESSIONS_ADMIN,
        icon: LogoutIcon,
      },
    ],
  },
];
